import { cva } from 'class-variance-authority';
import { cn } from '../../common/functions/cn';

export const buttonVariants = cva(
  cn(
    'group/button inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border',
    'font-medium text-sm',
    'select-none outline-none transition-all',
    // Popup triggers stay put: a button that shifts as its panel opens reads as jitter.
    '[&:active:not([aria-haspopup],[popovertarget],[commandfor])]:translate-y-px',
    'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
    'aria-invalid:border-error aria-invalid:ring-2 aria-invalid:ring-error/20 aria-invalid:focus-visible:border-error aria-invalid:focus-visible:ring-error/60 dark:aria-invalid:border-error/50 dark:aria-invalid:ring-error/50',
    'disabled:pointer-events-none disabled:opacity-50',
    "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
  ),
  {
    variants: {
      variant: {
        default: cn(
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/90',
          'aria-expanded:bg-primary/90 data-active:bg-primary/90'
        ),
        outline: cn(
          'border-border hover:bg-muted hover:text-foreground',
          'aria-expanded:border-ring aria-expanded:bg-accent aria-expanded:text-accent-foreground',
          'data-active:border-ring data-active:bg-accent data-active:text-accent-foreground'
        ),
        secondary: cn(
          'border-transparent bg-foreground text-background',
          '[--secondary-active:color-mix(in_oklch,var(--foreground),var(--background)_25%)] hover:bg-(--secondary-active)',
          'aria-expanded:bg-(--secondary-active) data-active:bg-(--secondary-active)'
        ),
        ghost: cn(
          'border-transparent hover:bg-muted hover:text-foreground',
          'aria-expanded:bg-accent aria-expanded:text-accent-foreground aria-expanded:hover:bg-accent',
          'data-active:bg-accent data-active:text-accent-foreground data-active:hover:bg-accent'
        ),
        destructive: cn(
          'border-transparent bg-error/10 text-error dark:bg-error/20',
          '[--destructive-ink:color-mix(in_oklch,var(--error),var(--foreground)_30%)] hover:bg-error/30 hover:text-(--destructive-ink)',
          'focus-visible:border-error/40 focus-visible:ring-error/20 dark:focus-visible:ring-error/40',
          'aria-expanded:bg-error/30 aria-expanded:text-(--destructive-ink)',
          'data-active:bg-error/30 data-active:text-(--destructive-ink)'
        ),
        link: 'border-transparent text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: cn('h-8 text-sm', 'gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2'),
        xs: "h-6 gap-1 px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: cn(
          'h-7 text-sm',
          "gap-1 px-2.5 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5"
        ),
        lg: cn('h-9 text-sm', 'gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2'),
        icon: 'size-8 text-sm',
        'icon-xs': "size-6 [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-7 text-sm',
        'icon-lg': 'size-9 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);
